import { createSelector } from 'reselect';
import { intersection } from 'ramda';

import { expandIntervalByMonths, expandIntervalByYears } from '../utils/time';
import { getLocationsWithDataIds } from './locations';
import { getSelectedLocationsIds } from './options';
import {
  ExportSource,
  Compare,
  Precision,
  ExportFileFormat,
  ExportTimeSource,
} from '../constants';
import { getApiIntervals, getTotalTimespanInterval } from './intervals';
import { getSafeTimeBounds } from '../selectors/timeBounds';
import { getCompare, getPrecision } from './options';

export const getIncludeDataset = state => state.exportDialog.includeDataset;
export const getAgreedToTerms = state => state.exportDialog.agreedToTerms;
export const getFileFormat = state => state.exportDialog.fileFormat;
// TODO: rename
export const getExportSource = state => state.exportDialog.exportSource;
export const getTimeSource = state => state.exportDialog.timeSource;
export const getIncludeMetadata = state => state.exportDialog.includeMetadata;
export const getExportDialogOpen = state => state.exportDialog.opened;
export const getDownloadsPending = state => state.exportDialog.downloadsPending;
export const getDownloadsFailed = state => state.exportDialog.downloadsFailed;

// TODO: census export

export const isExportDisabled = createSelector(
  getAgreedToTerms,
  getIncludeDataset,
  getIncludeMetadata,
  getDownloadsPending,
  (agreed, dataset, metadata, count) =>
    !agreed || (!dataset && !metadata) || count >= 1,
);

export const getExportCitiesCount = createSelector(
  getTimeSource,
  getExportSource,
  getLocationsWithDataIds,
  getSelectedLocationsIds,
  (timeSource, exportSource, withData, selected) =>
    exportSource === ExportSource.SELECTED &&
    timeSource === ExportTimeSource.SELECTED
      ? intersection(withData, selected).length
      : null,
);

export const getExportCompare = createSelector(
  getTimeSource,
  getCompare,
  (timeSource, compare) =>
    timeSource === ExportTimeSource.SELECTED ? compare : Compare.NONE,
);

export const getExportPrecision = createSelector(
  getTimeSource,
  getPrecision,
  (timeSource, precision) => {
    switch (timeSource) {
      case ExportTimeSource.SELECTED:
        return precision;

      case ExportTimeSource.ALL_MONTHLY:
        return Precision.MONTHLY;

      case ExportTimeSource.ALL_YEARLY:
        return Precision.YEARLY;

      case ExportTimeSource.ALL_MONTHLY_AND_YEARLY:
        return Precision.MONTHLY_AND_YEARLY;

      default:
        return Precision.MONTHLY_AND_YEARLY;
    }
  },
);

export const getExportTimespanInterval = createSelector(
  getTimeSource,
  getTotalTimespanInterval,
  getSafeTimeBounds,
  (timeSource, timespanInterval, timeBounds) =>
    timeBounds
      ? timeSource === ExportTimeSource.SELECTED ? timespanInterval : timeBounds
      : null,
);

export const getExportTimespans = createSelector(
  getTimeSource,
  getApiIntervals,
  getSafeTimeBounds,
  getExportPrecision,
  (timeSource, selectedTimespans, timeBounds, precision) => {
    if (!timeBounds) return [];
    switch (timeSource) {
      case ExportTimeSource.SELECTED:
        return selectedTimespans;

      case ExportTimeSource.ALL_MONTHLY:
        return expandIntervalByMonths(timeBounds);

      case ExportTimeSource.ALL_YEARLY:
      case ExportTimeSource.ALL_MONTHLY_AND_YEARLY:
        return expandIntervalByYears(timeBounds);

      default:
        return expandIntervalByYears(timeBounds);
    }
  },
);

export const getExportDatasetUrlParams = createSelector(
  getIncludeDataset,
  getFileFormat,
  getExportSource,
  getIncludeMetadata,
  getExportTimespans,
  getSelectedLocationsIds,
  getExportPrecision,
  getExportCompare,
  (
    includeDataset,
    fileFormat,
    exportSource,
    includeMetadata,
    timespans,
    selectedIds,
    precision,
    compare,
  ) => ({
    timespans,
    yearData: precision !== Precision.MONTHLY,
    monthData: precision !== Precision.YEARLY,
    incompleteAllowed: compare === Compare.RECURRING,
    format: fileFormat === ExportFileFormat.CSV ? 'csv' : 'xlsx',
    ...(exportSource === ExportSource.ALL ? {} : { cityIds: selectedIds }),
  }),
);

export const getExportDatasetMime = createSelector(
  getFileFormat,
  format =>
    format === ExportFileFormat.CSV ? 'text/csv' : 'application/vnd.ms-excel',
);



// WEBPACK FOOTER //
// ./src/selectors/exportDialog.js